import React, { FC, useEffect, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import Box from 'transfix-ui/core/Box'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { csvToMatrix, TCsvMatrix, TCsvErrors } from 'apps/marketplace/utils/helpers/csv'
import { LeftArrowIcon, RightArrowIcon, UpArrowIcon, DownArrowIcon } from 'transfix-icons'

interface ICsvPreviewProps {
  file: File
  maxRows?: number
  maxColumns?: number
  errors?: TCsvErrors | null
}

const useTableStyles = makeStyles(theme => ({
  table: {
    borderCollapse: 'collapse',
    borderWidth: '1px',
    borderColor: brandColors.coolGray3,
    borderStyle: 'solid',
  },
  cell: {
    borderWidth: '1px',
    borderColor: brandColors.coolGray3,
    borderStyle: 'solid',
    padding: theme.spacing(1),
  },
  errorCell: {
    borderWidth: '1px',
    borderColor: brandColors.coolGray3,
    borderStyle: 'solid',
    padding: theme.spacing(1),
    backgroundColor: brandColors.errorLight,
  },
  focusedErrorCell: {
    borderWidth: '1px',
    borderColor: brandColors.coolGray3,
    borderStyle: 'solid',
    padding: theme.spacing(1),
    backgroundColor: brandColors.errorLight,
    boxShadow: `inset 0 0 0 1px ${brandColors.error}`,
  },
}))

export const CsvPreview: FC<ICsvPreviewProps> = ({
  errors,
  file,
  maxColumns = 10,
  maxRows = 10,
}) => {
  const classes = useTableStyles()
  const [matrix, setMatrix] = useState<TCsvMatrix>([])
  const [rowIdx, setRowIdx] = useState<number>(0)
  const [columnIdx, setColumnIdx] = useState<number>(0)
  const [currentErrorIdx, setCurrentErrorIdx] = useState<number | null>(null)

  const rowCount = matrix.length
  const columnCount = matrix.length > 0 ? matrix[0].length : 0

  const getErrorArray = (allErrors: TCsvErrors): number[][] => {
    const errorArray: number[][] = []

    Object.keys(allErrors).forEach(rowIdx => {
      Object.keys(allErrors[Number(rowIdx)]).forEach(columnIdx => {
        errorArray.push([Number(rowIdx), Number(columnIdx)])
      })
    })

    return errorArray
  }

  const errorArray = errors ? getErrorArray(errors) : []

  const getCurrentErrorMessage = () => {
    if (errors && currentErrorIdx !== null) {
      const currentError = errorArray[currentErrorIdx]
      const [rowIdx, columnIdx] = currentError
      if (errors[rowIdx] && errors[rowIdx][columnIdx]) {
        const errorMessage = errors[rowIdx][columnIdx]
        const totalErrors = errorArray.length
        return `Error ${currentErrorIdx + 1} of ${totalErrors} (Row ${rowIdx + 1}): ${errorMessage}`
      }
    }

    return ''
  }

  const currentErrorMessage = getCurrentErrorMessage()

  const parseCsv = async () => {
    const csvMatrix = await csvToMatrix(file)
    setMatrix(csvMatrix)
  }

  const moveLeft = () => setColumnIdx(Math.max(0, columnIdx - 1))
  const moveRight = () => setColumnIdx(Math.min(columnCount, columnIdx + 1))
  const moveUp = () => setRowIdx(Math.max(0, rowIdx - 1))
  const moveDown = () => setRowIdx(Math.min(rowCount, rowIdx + 1))

  const canMoveLeft = columnIdx > 0
  const canMoveRight = columnIdx < columnCount - maxColumns
  const canMoveUp = rowIdx > 0
  const canMoveDown = rowIdx < rowCount - maxRows

  const cellHasError = (cellRowIdx: number, cellColumnIdx: number): boolean => {
    const adjustedRowIdx = cellRowIdx + rowIdx
    const adjustedColumnIdx = cellColumnIdx + columnIdx
    return !!(errors && errors[adjustedRowIdx] && errors[adjustedRowIdx][adjustedColumnIdx])
  }

  const isFocusedError = (cellRowIdx: number, cellColumnIdx: number): boolean => {
    if (currentErrorIdx === null) {
      return false
    }

    const adjustedRowIdx = cellRowIdx + rowIdx
    const adjustedColumnIdx = cellColumnIdx + columnIdx

    const focusedError = errorArray[currentErrorIdx]
    const [focusedErrorRowIdx, focusedErrorColumnIdx] = focusedError

    return adjustedRowIdx === focusedErrorRowIdx && adjustedColumnIdx === focusedErrorColumnIdx
  }

  const getCellClass = (cellRowIdx: number, cellColumnIdx: number): string => {
    const hasError = cellHasError(cellRowIdx, cellColumnIdx)
    const focusedError = isFocusedError(cellRowIdx, cellColumnIdx)

    if (hasError && focusedError) {
      return classes.focusedErrorCell
    }

    if (hasError) {
      return classes.errorCell
    }

    return classes.cell
  }

  // Parse the CSV whenever a
  // new file prop is detected:
  useEffect(() => {
    parseCsv()
  }, [file])

  // If errors are present and the
  // matrix is already created,
  // jump to the first error:
  useEffect(() => {
    if (errors && matrix.length > 0) {
      const errorArray = getErrorArray(errors)
      if (errorArray.length > 0) {
        setCurrentErrorIdx(0)
      }
    }
  }, [errors, matrix])

  // Whenever the current error index changes,
  // adjust the table to make sure the associated
  // table cell is visible:
  useEffect(() => {
    if (currentErrorIdx !== null) {
      const currentError = errorArray[currentErrorIdx]
      const [errorRowIdx, errorColumnIdx] = currentError
      const maxRowIdx = Math.max(0, rowCount - maxRows)
      const maxColumnIdx = Math.max(0, columnCount - maxColumns)
      const newRowIdx = Math.min(errorRowIdx, maxRowIdx)
      const newColumnIdx = Math.min(errorColumnIdx, maxColumnIdx)
      setRowIdx(newRowIdx)
      setColumnIdx(newColumnIdx)
    }
  }, [currentErrorIdx])

  useEffect(() => {
    const keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && canMoveLeft) {
        moveLeft()
      } else if (e.key === 'ArrowRight' && canMoveRight) {
        moveRight()
      } else if (e.key === 'ArrowUp' && canMoveUp) {
        moveUp()
      } else if (e.key === 'ArrowDown' && canMoveDown) {
        // Prevent default scrolling:
        e.preventDefault()
        moveDown()
      }
    }

    document.addEventListener('keydown', keydownListener)

    return () => {
      document.removeEventListener('keydown', keydownListener)
    }
  }, [canMoveLeft, canMoveRight, canMoveUp, canMoveDown, moveLeft, moveRight, moveUp, moveDown])

  const getPreviousButtonDisabled = () => {
    if (currentErrorIdx !== null && errorArray.length > 0) {
      const currentError = errorArray[currentErrorIdx]
      const [currentRowIdx, currentColumnIdx] = currentError
      const [firstErrorRowIdx, firstErrorColumnIdx] = errorArray[0]

      return currentRowIdx === firstErrorRowIdx && currentColumnIdx === firstErrorColumnIdx
    }

    return false
  }

  const getNextButtonDisabled = () => {
    if (currentErrorIdx !== null && errorArray.length > 0) {
      const currentError = errorArray[currentErrorIdx]
      const [currentRowIdx, currentColumnIdx] = currentError
      const [lastErrorRowIdx, lastErrorColumnIdx] = errorArray[errorArray.length - 1]

      return currentRowIdx === lastErrorRowIdx && currentColumnIdx === lastErrorColumnIdx
    }

    return false
  }

  const goToNextError = () => {
    if (currentErrorIdx !== null) {
      setCurrentErrorIdx(Math.min(currentErrorIdx + 1, errorArray.length - 1))
    }
  }

  const goToPreviousError = () => {
    if (currentErrorIdx !== null) {
      setCurrentErrorIdx(Math.max(currentErrorIdx - 1, 0))
    }
  }

  return (
    <div>
      <Box mb={2} display='flex' justifyContent='space-between' alignItems='center'>
        <Box display='flex'>
          <Box>
            <IconButton
              Icon={UpArrowIcon}
              onClick={moveUp}
              disabled={!canMoveUp}
              data-test='csv-preview-move-up'
              size='large'
            />
          </Box>
          <Box ml={1}>
            <IconButton
              Icon={DownArrowIcon}
              onClick={moveDown}
              disabled={!canMoveDown}
              data-test='csv-preview-move-down'
              size='large'
            />
          </Box>
          <Box ml={1}>
            <IconButton
              Icon={LeftArrowIcon}
              onClick={moveLeft}
              disabled={!canMoveLeft}
              data-test='csv-preview-move-left'
              size='large'
            />
          </Box>
          <Box ml={1}>
            <IconButton
              Icon={RightArrowIcon}
              onClick={moveRight}
              disabled={!canMoveRight}
              data-test='csv-preview-move-right'
              size='large'
            />
          </Box>
        </Box>
        {errorArray.length > 1 && (
          <Box display='flex'>
            <Box>
              <Button
                color='tertiary'
                onClick={goToPreviousError}
                disabled={getPreviousButtonDisabled()}
                data-test='csv-previous-error'>
                Previous Error
              </Button>
            </Box>
            <Box ml={1}>
              <Button
                color='tertiary'
                onClick={goToNextError}
                disabled={getNextButtonDisabled()}
                data-test='csv-next-error'>
                Next Error
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      {errors && currentErrorMessage && (
        <Box mb={2}>
          <InlineMessage message={currentErrorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
        </Box>
      )}
      <table className={classes.table}>
        {matrix.slice(rowIdx, rowIdx + maxRows).map((row, rowIdx) => (
          // Since we don't know the contents of the CSV, there is
          // no meaningful data to use as a key other than the array index:
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIdx}>
            {row &&
              row.slice(columnIdx, columnIdx + maxColumns).map((cell, cellIdx) => (
                <td
                  className={getCellClass(rowIdx, cellIdx)}
                  data-test={`csv-table-${rowIdx}-${cellIdx}`}
                  // Since we don't know the contents of the CSV, there is
                  // no meaningful data to use as a key other than the array index:
                  // eslint-disable-next-line react/no-array-index-key
                  key={cellIdx}>
                  {cell.length > 20 ? `${cell.substring(0, 20)}...` : cell}
                </td>
              ))}
          </tr>
        ))}
      </table>
    </div>
  )
}
